// src/pages/TriageResults.js
//
// Route: /spainsnapshot/results/:publicToken — the one real Spain Tax
// Snapshot result page (ticket 034-firstyearpolish: this file replaces the
// old chapter-based page outright; there is no other page anymore).
//
// Originally built (ticket 023-snapshotredesign) as a compact editorial
// redesign of the chapter page — four zones, in order: "what stands out,"
// findings, timeline, stop. No closing summary, no closing CTA — the
// sidebar (ResultCTA, unchanged, hard constraint #2) covers that.
//
// All tax-logic/content is delegated to buildSnapshotFindings.js — this
// file is page shell + layout only.

import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { fetchPartnerNameByRef } from '../lib/partnerLookup'
import { useProductMode } from '../hooks/useProductMode'
import { fetchTriageSessionByToken } from '../lib/sessionByToken'
import DisclaimerBanner from '../components/DisclaimerBanner'
import ResultCTA from '../components/ResultCTA'
import SnapshotFinding from '../components/SnapshotFinding'
import SnapshotTimeline from '../components/SnapshotTimeline'
import buildSnapshotFindings from '../lib/buildSnapshotFindings'
import { ProductTag } from '../components/HomeLink'
import Wordmark from '../components/Wordmark'

// ─── Ownership-aware action panels — page-local, not exported. ────────────

function OwnedPanel({ hubCTA, publicToken, isPartnerLinked, partnerName, otherProductDone, isUsPerson, userEmail }) {
  const navigate = useNavigate()
  return (
    <>
      <div className="tr-panel">
        <div className="pm-saved-confirm">
          <span className="pm-saved-check">✓</span>
          <span className="u-label-strong">Saved to your account</span>
        </div>
        {hubCTA && (
          <button className="btn-primary tr-panel-btn pm-hub-cta" onClick={() => navigate(hubCTA.href)}>
            {hubCTA.label}
          </button>
        )}
      </div>
      <ResultCTA
        product="triage"
        publicToken={publicToken}
        otherProductDone={otherProductDone}
        isAuthenticated
        isPartnerLinked={isPartnerLinked}
        partnerName={partnerName}
        isUsPerson={isUsPerson}
        userEmail={userEmail}
      />
    </>
  )
}

function UnclaimedPanel({ publicToken, onClaimed, isPartnerLinked, partnerName, otherProductDone, isUsPerson, userEmail }) {
  const [claiming, setClaiming] = useState(false)
  const [claimed,  setClaimed]  = useState(false)
  const [error,    setError]    = useState(null)
  const navigate = useNavigate()

  async function handleClaim() {
    setClaiming(true); setError(null)
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession()
      if (!authSession) throw new Error('Not authenticated')
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/claim-session`,
        {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${authSession.access_token}`,
          },
          body: JSON.stringify({ product: 'triage', public_token: publicToken }),
        }
      )
      if (!res.ok) throw new Error('Claim failed')
      setClaimed(true)
      onClaimed?.()
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setClaiming(false)
    }
  }

  return (
    <>
      <div className="tr-panel">
        {claimed ? (
          <>
            <div className="pm-saved-confirm">
              <span className="pm-saved-check">✓</span>
              <span className="u-label-strong">Saved to your account</span>
            </div>
            <button className="btn-primary tr-panel-btn pm-hub-cta" onClick={() => navigate('/intelligence')}>
              Go to Intelligence Hub →
            </button>
          </>
        ) : (
          <>
            <div className="u-eyebrow">Save your snapshot</div>
            <h2 className="tr-panel-title">Save to your account</h2>
            <p className="tr-panel-body">
              You're signed in. Save this snapshot to your account with one click - no email needed.
            </p>
            <button className="btn-primary tr-panel-btn" onClick={handleClaim} disabled={claiming}>
              {claiming ? 'Saving…' : 'Save to my account →'}
            </button>
            {error && <p className="triage-email-error triage-email-error--spaced">{error}</p>}
          </>
        )}
      </div>
      <ResultCTA
        product="triage"
        publicToken={publicToken}
        otherProductDone={otherProductDone}
        isAuthenticated
        isPartnerLinked={isPartnerLinked}
        partnerName={partnerName}
        isUsPerson={isUsPerson}
        userEmail={userEmail}
      />
    </>
  )
}

function PublicPanel({ publicToken, isPartnerLinked, partnerName, otherProductDone, isUsPerson }) {
  async function handleSaveEmail(email) {
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback?triage_session_id=${publicToken}` },
    })
    if (otpError) throw new Error(otpError.message)
  }

  return (
    <ResultCTA
      product="triage"
      publicToken={publicToken}
      otherProductDone={otherProductDone}
      isAuthenticated={false}
      isPartnerLinked={isPartnerLinked}
      partnerName={partnerName}
      isUsPerson={isUsPerson}
      onSaveEmail={handleSaveEmail}
    />
  )
}

function ActionPanel({ publicToken, isPartnerLinked, partnerName, ownerState, onClaimed, hubCTA, otherProductDone, isUsPerson, userEmail }) {
  if (ownerState === 'owned') {
    return (
      <OwnedPanel
        hubCTA={hubCTA}
        publicToken={publicToken}
        isPartnerLinked={isPartnerLinked}
        partnerName={partnerName}
        otherProductDone={otherProductDone}
        isUsPerson={isUsPerson}
        userEmail={userEmail}
      />
    )
  }
  if (ownerState === 'unclaimed') {
    return (
      <UnclaimedPanel
        publicToken={publicToken}
        onClaimed={onClaimed}
        isPartnerLinked={isPartnerLinked}
        partnerName={partnerName}
        otherProductDone={otherProductDone}
        isUsPerson={isUsPerson}
        userEmail={userEmail}
      />
    )
  }
  return (
    <PublicPanel
      publicToken={publicToken}
      isPartnerLinked={isPartnerLinked}
      partnerName={partnerName}
      otherProductDone={otherProductDone}
      isUsPerson={isUsPerson}
    />
  )
}

// ─── Main page ────────────────────────────────────────────────────────────

export default function TriageResults() {
  const { publicToken } = useParams()
  const { ownershipState, hubCTA, user, otherProductDone } = useProductMode()

  const [sessionData, setSessionData] = useState(null)
  const [partner,     setPartner]     = useState(null)
  const [uiState,     setUiState]     = useState('loading')
  const [loadError,   setLoadError]   = useState(null)
  const [ownerState,  setOwnerState]  = useState('public')
  const fetched = useRef(false)

  useEffect(() => {
    if (fetched.current || !publicToken) return
    fetched.current = true
    init()
  }, [])

  async function init() {
    const { data, error } = await fetchTriageSessionByToken(publicToken)

    if (error || !data) {
      setLoadError("We couldn't find that snapshot. It may have expired.")
      setUiState('error')
      return
    }

    setSessionData(data)
    setOwnerState(ownershipState({ is_owner: data.is_owner, is_claimed: data.is_claimed }))

    if (data.partner_ref) {
      const { data: partnerRow } = await fetchPartnerNameByRef(data.partner_ref)
      if (partnerRow) setPartner(partnerRow)
    }

    setUiState('ready')
  }

  if (uiState === 'loading') {
    return <div className="trc-root"><div className="trc-loading"><div className="app-spinner" /></div></div>
  }

  if (uiState === 'error') {
    return <div className="trc-root"><div className="tr-col"><div className="alert-error trc-load-error">{loadError}</div></div></div>
  }

  const result   = sessionData?.triage_result || {}
  const allFlags = result.flags || []
  const facts    = result.derived_facts || {}
  const taxYear  = facts.taxYear || new Date().getFullYear()
  const isUsPerson      = facts.isUsPerson ?? false
  const isPartnerLinked = !!sessionData?.partner_ref

  const { findings, notableChips, prose, timelineNodes } = buildSnapshotFindings(facts, allFlags, taxYear)

  const actionPanelProps = {
    publicToken,
    isPartnerLinked,
    partnerName: partner?.name,
    ownerState,
    onClaimed: () => setOwnerState('owned'),
    hubCTA,
    isUsPerson,
    userEmail: user?.email || null,
    otherProductDone: otherProductDone('triage'),
  }

  return (
    <div className="trc-root">
      <div className="trc-nav">
        <Link to="/" className="triage-intro-wordmark triage-intro-wordmark--flush">
          <Wordmark accentClassName="triage-start-wordmark__accent" />
        </Link>
        <ProductTag product="triage" partnerName={partner?.name} />
      </div>

      <div className="trc-grid">
        <main className="trc-main">
          <h1 className="trc-hero-headline">Your {taxYear} Spain tax snapshot</h1>

          <div className="trc-standout">
            <div className="trc-standout-eyebrow">
              <span className="trc-standout-icon">✦</span>
              <div className="u-eyebrow">What stands out</div>
            </div>
            {notableChips ? (
              <div className="trc-standout-chips">
                {notableChips.map(chip => <span className="trc-standout-chip" key={chip}>{chip}</span>)}
              </div>
            ) : (
              <p className="trc-standout-prose">{prose}</p>
            )}
          </div>

          <h2 className="trc-findings-title">Your {taxYear} Spain tax picture</h2>
          <div className="trc-findings-card">
            {findings.map((finding, i) => (
              <SnapshotFinding key={finding.id} index={i + 1} finding={finding} />
            ))}
          </div>

          {timelineNodes.length > 0 && (
            <SnapshotTimeline nodes={timelineNodes} taxYear={taxYear} />
          )}

          <DisclaimerBanner variant="banner" className="trc-disclaimer" />

          <div className="trc-panel-mobile">
            <ActionPanel {...actionPanelProps} />
          </div>
        </main>

        <aside className="trc-aside">
          <ActionPanel {...actionPanelProps} />
        </aside>
      </div>
    </div>
  )
}
